<template>
  <div class="flex items-center gap-2">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="outline" size="sm" type="button" class="h-7 rounded-full border-transparent bg-muted px-3 text-xs font-semibold text-muted-foreground shadow-none ring-1 ring-border hover:bg-transparent" :loading="isUpdating">
          <span v-if="showLabel" class="text-xs text-muted-foreground">
            Deadline
          </span><span class="truncate">{{ deadlineLabel }}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-56">
        <DropdownMenuLabel>Change Deadline:</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetDeadline('today')">
          Today
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetDeadline('tomorrow')">
          Tomorrow
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetDeadline('end_of_week')">
          End of Week
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetDeadline('next_week')">
          Next Week
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="openCustomModal">
          Custom
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <TaskCustomDeadlineModal
      ref="customDeadlineModal"
      :task-id="taskId"
      :value="value"
      :context="context"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import TaskCustomDeadlineModal from './TaskCustomDeadlineModal.vue'
import { useTasksStore } from '../../../stores/tasksStore.js'
import { buildTaskPresetDate, formatTaskFieldDateTimeLabel } from '../../../utils/taskFieldDateTime.js'

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  value: {
    type: [String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'default',
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
})

const customDeadlineModal = ref(null)

const tasksStore = useTasksStore(props.context)
const taskId = computed(() => Number(props.task?.id))
const isUpdating = computed(() => tasksStore.isUpdatingDeadline(taskId.value))
const deadlineLabel = computed(() => formatTaskFieldDateTimeLabel(props.value, 'No deadline'))

const updateTaskDeadline = async (deadlineAt, successMessage = 'Deadline updated.') => {
  if (!Number.isInteger(taskId.value) || taskId.value <= 0) {
    return false
  }

  try {
    await tasksStore.updateDeadline(taskId.value, deadlineAt)
    toast.success(successMessage)
    return true
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.deadline_at?.[0]
      || 'Unable to update the deadline.'
    toast.error(errorMessage)
    return false
  }
}

const setPresetDeadline = async (preset) => {
  const presetDate = buildTaskPresetDate(preset, preset === 'next_week' ? 9 : 17)

  if (!presetDate) {
    return
  }

  const successMessagesByPreset = {
    today: 'Deadline set to today.',
    tomorrow: 'Deadline set to tomorrow.',
    end_of_week: 'Deadline set to end of week.',
    next_week: 'Deadline set to next week.',
  }

  await updateTaskDeadline(
    presetDate.toISOString(),
    successMessagesByPreset[preset] ?? 'Deadline updated.'
  )
}
const openCustomModal = () => {
  customDeadlineModal.value?.openModal?.()
}
</script>
